import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { SingleOrArray } from '../../../../models/defines';
import { IWSContext, IWSInDTO } from '../../../../models/workspace/workspace-model';
import WorkspaceAssetsUrls from '../../../../constants/private/urls/workspace/associations/assets/workspace-assets.urls';
import {
    IGenerateImageRequest,
    IGenerateVideoRequest,
    IMoveAssets,
    IRenameAsset,
} from '../../../http-models/asset/asset-http-request-model';
import WorkspaceAssetsDetailsUrls from '../../../../constants/private/urls/workspace/associations/assets/workspace-assets-details.urls';
import { IExternalLibraryUpload } from '../../../shared/utils/asset-utils.service';
import { IAssetInDTO, ILocalAsset } from '../../../../models/assets/assets.model';
import { IAssetsResponse, IGetAssetsParams } from './workspace-asset-request.types';

@Injectable()
export class WorkspaceAssetApiService {
    constructor(private http: HttpClient) {}

    upload$ = (formData: FormData, wsContext: IWSContext, extraParams = {}) => {
        const url = WorkspaceAssetsUrls.getUploadAssetPOSTUrl(wsContext);
        let httpParams = new HttpParams();

        // Add extra query parameters dynamically
        Object.keys(extraParams).forEach((key) => {
            httpParams = httpParams.set(key, extraParams[key]);
        });
        return this.http.post(url, formData, {
            reportProgress: true,
            observe: 'events',
            params: httpParams,
        });
    };

    createFolder$(wsContext: IWSContext, folderName: string) {
        const url = WorkspaceAssetsUrls.getCreateFolderPOSTUrl(wsContext);
        return this.http.post<IAssetInDTO>(url, { newName: folderName });
    }
    renameFileAsset$(wsContext: IWSContext, data: IRenameAsset) {
        const url = WorkspaceAssetsDetailsUrls.getUpdateFileNamePUTUrl(wsContext, data.assetId);
        return this.http.put<IAssetInDTO>(url, { newName: data.newName });
    }
    moveAssetToFolder$(wsContext: IWSContext, moveAssetPayload: IMoveAssets) {
        const url = WorkspaceAssetsUrls.getMoveFilePUTUrl(wsContext);
        return this.http.put<IAssetInDTO>(url, moveAssetPayload);
    }
    getAssetsInWorkspace$(wsContext: IWSContext, params: IGetAssetsParams = {}) {
        const url = WorkspaceAssetsUrls.getAssetsListGETUrl(wsContext);
        let httpParams = new HttpParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                httpParams = httpParams.set(key, String(value));
            }
        });
        return this.http.get<IAssetsResponse>(url, { params: httpParams });
    }
    getAssetsByIdsAsync$(wsContext: IWSContext, assetIds: SingleOrArray<string>) {
        const url = WorkspaceAssetsUrls.getSpecificAssetByIdsGETUrl(wsContext);

        const ids = Array.isArray(assetIds) ? assetIds : [assetIds];

        let params = new HttpParams();
        ids.forEach((assetId) => {
            params = params.append('assetIds', assetId);
        });

        return this.http.get<IAssetInDTO[]>(url, { params });
    }

    removeAssetsFromWS$(wsContext: IWSContext, assetIds: SingleOrArray<string>) {
        return this.http.delete<{ workspace: IWSInDTO }>(WorkspaceAssetsUrls.getRemoveAssetsDELETEUrl(wsContext), {
            body: {
                assetIds: assetIds,
            },
        });
    }

    uploadPhotoFromPhotoLibrary$(wsContext: IWSContext, body: IExternalLibraryUpload, toFolder: string) {
        const url = WorkspaceAssetsUrls.getUploadPhotoFromPhotoLibrary(wsContext);
        return this.http.post<string>(url, { ...body, toFolder });
    }

    generateVideo$(wsContext: IWSContext, body: IGenerateVideoRequest) {
        const url = WorkspaceAssetsUrls.getGenerateVideoPOSTUrl(wsContext);
        return this.http.post<{ success: true }>(url, body);
    }

    generateImage$(wsContext: IWSContext, body: IGenerateImageRequest) {
        const url = WorkspaceAssetsUrls.getGenerateImagePOSTUrl(wsContext);
        return this.http.post<{ success: true; uploadPath: string; assetId: string; asset: ILocalAsset }>(url, body);
    }
}
